import * as React from 'react';
import { cloneDeep, debounce } from 'lodash';
import { DeleteOutlined } from '@ant-design/icons';
import { message, Space, Switch } from 'antd';
import {
  DialogAct,
  DiffStates,
  Reference,
  ReferenceNode,
  FrameItem,
  ParameterType,
  ParameterConfig,
  Attribute,
  dialogActToServer,
  PromptInfo,
  PromptType,
  PromptOwnerType,
  InputParameter,
  ActionType,
  FramelyAction,
  ClearSlotAction,
  IntentInfo
} from '@interface/index';
import {
  NiSpin,
  BodyGroup,
  NiTable,
  NiModal,
  AddButton,
  PromptListComp,
  NiForm,
  NiSelect,
  discardModal,
  InputParameterComp,
  NiRadio,
  ReferenceTextArea,
  NiCard,
  NiButton,
  HelperPopover
} from '@component/index';
import {
  NiCommonCascader,
  SELECTION_KEY,
  SelectionOption,
  API,
  getSelectionOptions,
  SourceType
} from '@config/index';
import { localizedString } from '@locale/index';
import {
  ParameterMapStore,
  isEqual,
  attributeToParameter,
  getAgentId,
  isStructAgent,
  getOrgInfo
} from '@lib/index';
import { AnnotationOperator } from '@page/action-parameter/component/annotation-operator';

import '@style/component/value-check/index.less';
import { NewPromptList, PromptListHandle } from '@component/prompt/new-prompt-list';

interface DialogActTableProps {
  disabled?: boolean;
  frame: FrameItem;
  forSlot?: Attribute;
  data: DialogAct[];
  parameters: ParameterConfig[];
  selectedParameters: Attribute[];
  didChange?: (data: DialogAct[]) => void;
  onChange?: (data: DialogAct[]) => void;
  cardDeleteClick?: () => void;

  showSwitch?: boolean;
  switchChecked?: boolean;
  onSwitchChange?: (checked: boolean) => void;
}

interface DialogActTableState {
  availableDialogActIntents: SelectionOption[];
  isNew: boolean;
  loading: boolean;
  index: number;
  data: DialogAct[];
  delData: DialogAct[];
  modalData?: DialogAct;
  diffVisible: boolean;
  diffMaster?: any;
  diffLocal?: any;
  diffRevert?: () => void;
}


export class DialogActTable extends React.Component<DialogActTableProps, DialogActTableState> {
  constructor(props: DialogActTableProps) {
    super(props);

    this.state = {
      availableDialogActIntents: [],
      loading: false,
      isNew: false,
      index: -1,
      data: [],
      delData: [],
      diffVisible: false
    };
  }

  componentDidMount() {
    this.onInit();
  }

  shouldComponentUpdate(nextProps: DialogActTableProps, nextState: DialogActTableState, nextContext: any) {

    if (!isEqual(nextProps.data, this.props.data) || !isEqual(nextProps.forSlot, this.props.forSlot)) {
      setTimeout(this.onInit, 100);

      return true;
    }

    return !isEqual(nextState, this.state);
  }

  onInit = async () => {
    const props = this.props;

    const { forSlot } = props;

    const selectionRoot = [{
      value: props.frame.id || '',
      value_type: props.frame.type
    }];

    if (forSlot) {
      selectionRoot.push({
        value: forSlot.id || '',
        value_type: ParameterType.ATTRIBUTE as any
      });
    }

    this.setState({
      data: props.data,
      availableDialogActIntents: await getSelectionOptions(
        SELECTION_KEY.AVAILABLE_DIALOG_ACT_INTENT,
        selectionRoot
      )
    });
  }

  defaultModalData = (): DialogAct => {
    const { frame, forSlot } = this.props;
    return {
      ownerId: forSlot ? forSlot.id : frame.id,
      ownerType: forSlot ? ParameterType.ATTRIBUTE : frame.type,
      frameId: forSlot ? forSlot.ownerId : frame.id,
      id: '',
      prompts: [],
      state: DiffStates.ADD
    };
  }

  promptRef = React.createRef<PromptListHandle>();

  render() {
    const {
      loading,
      data,
      modalData,
      availableDialogActIntents
    } = this.state;
    const { frame, selectedParameters, disabled, forSlot, showSwitch, switchChecked, onSwitchChange } = this.props;


    const modalDisabled = modalData && disabled;
    return <NiSpin spinning={loading}>
      <BodyGroup
        noPadding
        className={`slot-suggestion-card ${!switchChecked && 'no-content'}`}
        title={localizedString('dialog_act')}
        titleExtra={
          <Space size={12}>
            <HelperPopover localizedKey="slot_detail_helper_dialog_act" />
            {
              showSwitch && <Switch disabled={disabled || !!frame.originKey} checked={switchChecked} onChange={onSwitchChange} />
            }
          </Space>
        }
        extra={[
          (switchChecked || !forSlot) &&
          <AddButton
            disabled={disabled || !!frame.originKey}
            onClick={() => {
              this.setState({
                modalData: this.defaultModalData(),
                isNew: true
              });
            }} />
          //   ,
          // forSlot && <AnnotationOperator
          //   disabled={disabled  || (!!forSlot && !!frame.originKey)}
          //   onDelete={() => {
          //     if (this.props.cardDeleteClick) {
          //       this.props.cardDeleteClick();
          //     }
          //   }}
          // />
        ]
        }
      >
        {modalData && <NiModal
          title={localizedString('dialog_act')}
          width={'60%'}
          wrapClassName="dialog-act-modal"
          open={!!modalData}
          onCancel={this.handleModalCancel}
          onOk={modalDisabled ? undefined : this.handleModalConfirm}
          okButtonProps={modalDisabled ? {
            type: 'ghost',
            disabled: true
          } : undefined}
        >
          <NiForm
            titleline
            direction="vertical"
            items={[
              {
                key: 'intent',
                label: localizedString('dialog_act'),
                content: <NiSelect
                  style={{ width: '100%' }}
                  disabled={disabled || !!frame.originKey}
                  value={modalData.intent && modalData.intent.id}
                  onChange={(_: string, opt: any) => {
                    this.setState({
                      modalData: {
                        ...modalData,
                        intent: {
                          id: opt.value,
                          name: opt.label,
                          type: ParameterType.INTENT
                        }
                      }
                    });
                  }}
                >
                  {
                    availableDialogActIntents.map((opt: SelectionOption) =>
                      <NiSelect.Option key={opt.value} value={opt.value as string}>
                        {opt.label}
                      </NiSelect.Option>)
                  }
                </NiSelect>
              },
              // getOrgInfo().name === 'io.framely' &&
              {
                key: 'prompts',
                label: localizedString('attribute_prompts'),
                extra: modalData.prompts && modalData.prompts.filter(item => !item.toBeDeleted).length && <AddButton
                  disabled={disabled || isStructAgent()}
                  onClick={(e: any) => {
                    this.promptRef.current?.add(e);
                  }} /> || '',
                content: <NewPromptList
                  inModal
                  ref={this.promptRef}
                  style={{ flex: 1 }}
                  agentId={getAgentId()}
                  frameId={frame.id || ''}
                  ownerId={modalData.id || ''}
                  ownerType={PromptOwnerType.dialogAct}
                  promptType={PromptType.prompt}
                  frame={frame}
                  disableChange={isStructAgent() || modalDisabled}
                  // placeholderFrist={localizedString('attribute_question_placeholder_prompt', { slot: '{slot}' })}
                  noReference={false}
                  isSingleLine={false}
                  allowClearAll={true}
                  data={modalData.prompts || []}
                  parameterMap={ParameterMapStore.all()}
                  parameters={selectedParameters.map(attributeToParameter)}
                  update={(prompts: PromptInfo[]) => {
                    const nextModalData = cloneDeep(modalData);
                    nextModalData.prompts = prompts;
                    this.setState({
                      modalData: nextModalData
                    });
                  }}
                />
              }
            ]}
          />
        </NiModal>
        }
        {switchChecked &&
          <NiTable
            rowKey={(record: DialogAct) => `dialog-act-${record.id}`}
            dataSource={data}
            pagination={false}
            onRow={(record: DialogAct, index?: number) => ({
              onClick: () => {
                const modalData = cloneDeep(record);
                this.setState({
                  modalData,
                  index: index || 0,
                  isNew: false
                });
              }
            })}
            columns={this.getColumns()}
          />
        }
      </BodyGroup >
    </NiSpin >;
  }

  getColumns = () => {
    const { disabled } = this.props;
    return [
      {
        // width: '90%',
        title: localizedString('dialog_act'),
        dataIndex: ['intent', 'name']
      },
      {
        width: 150,
        title: 'Source',
        dataIndex: 'inheritedFromCompositeName',
        key: 'source',
        render: (sourceName: string, record: DialogAct) => {
          return sourceName || (record.isOverwrite ? 'Local+' : 'Local');
        }
      },
      // admin
      {
        title: '',
        className: 'hide',
        key: 'operation',
        width: 70,
        render: (
          operation: any,
          record: DialogAct,
          index: number
        ) => {
          return (
            <div className="icon-area">
              {!disabled &&
                !record.originKey &&
                <DeleteOutlined
                  className="delete-btn"
                  onClick={(e: any) => {
                    e.stopPropagation();
                    this.handleDelete(record, index);
                  }}
                />
              }
            </div>
          );
        }
      }
    ];
  };

  handleDelete = async (record: DialogAct, index: number) => {
    const props = this.props;
    let nextData = cloneDeep(this.state.data);

    if (props.forSlot && props.onChange) {

      nextData = nextData.map((item: DialogAct, i: number) =>
        i === index ? {
          ...item,
          toBeDeleted: true
        } : item);
      props.onChange(nextData);
      return;
    }

    this.setState({ loading: true });

    await API.dialogAct.delete({
      metaID: record.id || '',
      ownerID: record.ownerId,
      frameID: record.frameId
    }).catch((err: any) => {
      message.error(err.message);
      this.setState({ loading: false });
      throw new Error(err.message);
    });

    this.setState({ loading: false });

    nextData.splice(index, 1);
    this.setState({
      data: nextData
    }, () => {
      if (props.didChange) {
        props.didChange(nextData);
      }
    });
  }

  modalDirtyCheck = () => {
    const { isNew, modalData, index, data } = this.state;

    if (!modalData) {
      return false;
    }

    const oriData = isNew ? this.defaultModalData() : data[index];
    return !isEqual(oriData.intent, modalData.intent) ||
      !isEqual(oriData.prompts, modalData.prompts);
  }

  handleModalCancel = () => {
    if (this.modalDirtyCheck()) {
      discardModal(() => {
        this.setState({ modalData: undefined });
      });
      return;
    }
    this.setState({ modalData: undefined });
  }

  handleModalConfirm = debounce(async () => {
    const { modalData, data, isNew, index } = this.state;
    const props = this.props;

    if (!modalData) {
      return;
    }

    if (!modalData.intent) {
      message.warning('Dialog act is required!');
      return;
    }

    const nextItem = cloneDeep(modalData);
    nextItem.prompts = modalData.prompts.filter(Boolean);

    const payload = dialogActToServer(nextItem);

    if (isNew) {

      const nextData = cloneDeep(data);

      if (props.forSlot && props.onChange) {
        props.onChange([
          ...props.data, nextItem
        ]);
        this.setState({
          modalData: undefined
        });
        return;
      }

      this.setState({ loading: true });

      await API.dialogAct.create(payload).catch((err: any) => {
        message.error(err.message);
        this.setState({ loading: false });
        throw new Error(err.message);
      });

      this.setState({ loading: false });

      nextData.push(nextItem);

      this.setState({
        data: nextData,
        modalData: undefined
      }, () => {
        if (props.didChange) {
          props.didChange(nextData);
        }
      });
    } else {

      if (props.forSlot && props.onChange) {

        let nextData = cloneDeep(props.data);
        nextData = nextData.map((item: DialogAct) =>
          item.id === nextItem.id ? nextItem : item);
        props.onChange(nextData);
        this.setState({
          modalData: undefined
        });
        return;
      }

      await API.dialogAct.update({
        metaID: nextItem.id || '',
        ownerID: props.frame.id,
        ownerType: props.frame.type,
        frameID: props.frame.id
      },
        payload,
        nextItem.inheritedFromCompositeId
      ).catch((err: any) => {
        message.error(err.message);
        this.setState({ loading: false });
        throw new Error(err.message);
      });

      this.setState({ loading: false });

      const nextData = cloneDeep(data);
      nextData[index] = nextItem;

      this.setState({
        data: nextData,
        modalData: undefined
      }, () => {
        if (props.didChange) {
          props.didChange(nextData);
        }
      });
    }
  }, 500);
}
